'use client';

import Link from 'next/link';
import PropTypes from 'prop-types';
import { Box, Container, Grid, Typography } from '@mui/material';
import WebsiteInteriorScrollLayout from './WebsiteInteriorScrollLayout';
import NewsMediaPageHeader from './NewsMediaPageHeader';

function PrintPublicationCard({ publication }) {
  const { coverSrc } = publication;

  return (
    <Box
      component={Link}
      href={`/print/${publication.slug}`}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        '&:hover .print-card-image': {
          opacity: 0.88,
        },
      }}
    >
      {coverSrc ? (
        <Box
          className="print-card-image"
          component="img"
          src={coverSrc}
          alt={`${publication.title} ${publication.date}`}
          sx={{
            width: '100%',
            height: { xs: 280, sm: 320, md: 340 },
            objectFit: 'cover',
            display: 'block',
            transition: 'opacity 0.3s ease',
          }}
        />
      ) : (
        <Box
          className="print-card-image"
          sx={{
            width: '100%',
            height: { xs: 280, sm: 320, md: 340 },
            bgcolor: 'grey.200',
          }}
          aria-hidden
        />
      )}
      <Box sx={{ pt: 1.5 }}>
        <Typography component="h2" variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
          {publication.title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {publication.date}
        </Typography>
      </Box>
    </Box>
  );
}

PrintPublicationCard.propTypes = {
  publication: PropTypes.shape({
    slug: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    date: PropTypes.string.isRequired,
    coverSrc: PropTypes.string.isRequired,
  }).isRequired,
};

export default function PrintPage({ publications }) {
  return (
    <WebsiteInteriorScrollLayout>
      <NewsMediaPageHeader title="NIICO in Print" />
      <Box component="section" sx={{ pb: { xs: 6, md: 10 } }}>
        <Container maxWidth="lg">
          {publications.length === 0 ? (
            <Typography color="text.secondary">No print publications available yet.</Typography>
          ) : (
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {publications.map(publication => (
                <Grid key={publication.slug} item xs={12} sm={6} md={3}>
                  <PrintPublicationCard publication={publication} />
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
    </WebsiteInteriorScrollLayout>
  );
}

PrintPage.propTypes = {
  publications: PropTypes.arrayOf(
    PropTypes.shape({
      slug: PropTypes.string.isRequired,
      title: PropTypes.string.isRequired,
      date: PropTypes.string.isRequired,
      coverSrc: PropTypes.string.isRequired,
    }),
  ).isRequired,
};
